import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { removeFamilyRelationship } from "../../store/thunks/clientsThunk";
import { logInfo, logError } from "../../utils/logger";

export default function UnlinkFamilyMemberModal({
  memberType,
  clientId,
  familyMembers,
  onUnlink = () => {},
  onClose,
  onCancel,
  onUpdateClient,
}) {
  const [selectedMember, setSelectedMember] = useState(null);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const dispatch = useDispatch();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedMember) {
      setFeedback({
        type: "error",
        message: "Please select a family member to unlink."
      });
      return;
    }

    try {
      const result = await dispatch(
        removeFamilyRelationship({
          parentId: memberType === "parent" ? selectedMember._id : clientId,
          childId: memberType === "parent" ? clientId : selectedMember._id,
        })
      ).unwrap();
      
      // Check if the response indicates success
      if (result?.message === "Parent-child relationship removed successfully") {
        setFeedback({
          type: "success",
          message: `Successfully unlinked ${selectedMember.firstName} ${selectedMember.lastName}`
        });
        
        // Safely call onUnlink if it exists
        onUnlink?.({ selectedMember, relation: memberType });
        await onUpdateClient(); // Wait for the client update
        
        // Close the modal after a short delay to show the success message
        setTimeout(() => {
          onClose();
        }, 1500);

        logInfo("Family relationship removed successfully", {
          memberType,
          clientId,
          selectedMemberId: selectedMember._id,
        });
      } else {
        setFeedback({
          type: "error",
          message: "Failed to unlink family member. Please try again."
        });
      }
    } catch (error) {
      console.error("Error removing family relationship:", error);
      setFeedback({
        type: "error",
        message: error.message || "Failed to unlink family member. Please try again."
      });
      logError("Error removing family relationship", {
        memberType,
        clientId,
        selectedMemberId: selectedMember._id,
        error: error.message,
      });
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-sm shadow-lg w-[400px] max-w-[95vw] p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {feedback.message && (
            <div 
              className={`p-3 rounded-sm text-sm ${
                feedback.type === "success" 
                  ? "bg-green-50 text-green-700 border border-green-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {feedback.message}
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Unlink {memberType}
            </h2>
            <p className="text-sm text-gray-500">
              Select the {memberType} to unlink.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Select {memberType} to unlink
            </label>
            <select
              className="w-full h-9 rounded-sm border border-gray-200 bg-white px-3 py-1 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              onChange={(e) =>
                setSelectedMember(familyMembers[e.target.value])
              }>
              <option value="">Select a {memberType}</option>
              {familyMembers.map((member, index) => (
                <option key={member._id} value={index}>
                  {member.firstName} {member.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-8 items-center justify-center rounded-sm border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100">
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex h-8 items-center justify-center rounded-sm bg-red-600 px-4 text-sm font-medium text-white transition-colors hover:bg-red-500">
              Unlink
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
